import { useEffect, useRef, useState } from "react";
import { api, fmtEUR } from "@/lib/api";
import FileUpload from "@/components/FileUpload";
import { Plus, Pencil, Trash2, Upload, X, Search, Download, FileDown, Calculator } from "lucide-react";
import { toast } from "sonner";
import BulkMarginModal from "@/components/BulkMarginModal";

const EMPTY = {
    sku: "", name: "", brand: "", category_slug: "", price: 0, stock: 0,
    description: "", short_description: "", image_url: "", compatibility: [],
    is_original: false, is_refurbished: false, is_featured: false, active: true,
    manual_price: false, manually_disabled: false,
    sync_no_all: false, sync_no_stock: false, sync_no_price: false,
    sync_no_cost: false, sync_no_name: false, sync_no_printers: false, sync_no_data: false,
    cost_price: null, supplier_price: null,
    color: "", yield_value: null, yield_unit: "",
};

const PAGE_SIZE = 50;

export default function AdminProducts() {
    const [products, setProducts] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [search, setSearch] = useState("");
    const [brandFilter, setBrandFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [editing, setEditing] = useState(null);
    const [bulkOpen, setBulkOpen] = useState(false);
    const fileRef = useRef(null);

    const buildQuery = (extra = {}) => {
        const q = new URLSearchParams();
        if (search) q.set("search", search);
        if (brandFilter) q.set("brand", brandFilter);
        if (categoryFilter) q.set("category", categoryFilter);
        Object.entries(extra).forEach(([k, v]) => q.set(k, v));
        return q.toString();
    };

    const load = () => {
        const list = buildQuery({ limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE });
        api.get(`/products?${list}`).then((r) => setProducts(r.data));
        api.get(`/products/count?${buildQuery()}`).then((r) => setTotal(r.data.total));
    };

    useEffect(() => {
        api.get("/categories").then((r) => setCategories(r.data));
        api.get("/products/brands").then((r) => setBrands(r.data.brands || []));
    }, []);
    // reset page on filter change
    useEffect(() => { setPage(1); }, [search, brandFilter, categoryFilter]);
    useEffect(() => {
        const t = setTimeout(load, 300);
        return () => clearTimeout(t);
    }, [search, brandFilter, categoryFilter, page]);

    const save = async (data) => {
        const payload = { ...data, price: Number(data.price), stock: Number(data.stock),
            compatibility: typeof data.compatibility === "string"
                ? data.compatibility.split(",").map((c) => c.trim()).filter(Boolean)
                : data.compatibility };
        try {
            if (data.id) await api.put(`/admin/products/${data.id}`, payload);
            else await api.post("/admin/products", payload);
            toast.success("Produto guardado");
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro");
        }
    };

    const del = async (id) => {
        if (!window.confirm("Eliminar produto?")) return;
        await api.delete(`/admin/products/${id}`);
        toast.success("Eliminado");
        load();
    };

    const importCSV = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/admin/products/import-csv", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Importado: ${data.created} criados, ${data.updated} actualizados`);
            load();
        } catch (err) {
            toast.error("Erro na importação CSV");
        }
        e.target.value = "";
    };

    const manualFileRef = useRef(null);
    const manualImport = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/admin/products/manual-import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 600000,
            });
            const errMsg = data.errors?.length ? ` · ${data.errors.length} erros` : "";
            toast.success(`${data.updated} produtos actualizados, ${data.skipped} ignorados${errMsg}`, { duration: 7000 });
            load();
        } catch (err) {
            toast.error("Erro a importar CSV manual");
        }
        e.target.value = "";
    };

    const exportCSV = async () => {
        const params = new URLSearchParams();
        if (search) params.set("search", search);
        if (brandFilter) params.set("brand", brandFilter);
        if (categoryFilter) params.set("category", categoryFilter);
        try {
            const r = await api.get(`/admin/products/export.csv?${params.toString()}`, { responseType: "blob" });
            const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const tag = brandFilter || categoryFilter || "todos";
            a.download = `produtos_${tag}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Exportação concluída");
        } catch (err) {
            toast.error("Erro a exportar");
        }
    };

    return (
        <div data-testid="admin-products-page">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <h1 className="font-display font-black text-4xl tracking-tighter">Produtos</h1>
                <div className="flex gap-2 flex-wrap">
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".csv"
                        onChange={importCSV}
                        className="hidden"
                        data-testid="csv-input"
                    />
                    <input
                        ref={manualFileRef}
                        type="file"
                        accept=".csv"
                        onChange={manualImport}
                        className="hidden"
                        data-testid="manual-csv-input"
                    />
                    <button onClick={() => setBulkOpen(true)} className="btn-secondary" data-testid="bulk-margin-btn" title="Aplicar margem a vários produtos de uma vez">
                        <Calculator size={16} /> Aplicar margem
                    </button>
                    <button onClick={exportCSV} className="btn-secondary" data-testid="export-csv-btn" title="Exporta produtos filtrados em CSV">
                        <FileDown size={16} /> Exportar CSV
                    </button>
                    <button onClick={() => manualFileRef.current?.click()} className="btn-secondary" data-testid="manual-import-btn" title="Re-upload do CSV exportado para actualização manual em massa">
                        <Download size={16} className="rotate-180" /> Importar CSV manual
                    </button>
                    <button onClick={() => fileRef.current?.click()} className="btn-secondary" data-testid="import-csv-btn" title="Import livre (legado)">
                        <Upload size={16} /> Importar livre
                    </button>
                    <button onClick={() => setEditing(EMPTY)} className="btn-primary" data-testid="new-product-btn">
                        <Plus size={16} /> Novo Produto
                    </button>
                </div>
            </div>

            <div className="mb-4 flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[260px] max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" size={16} />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Procurar por SKU, nome ou marca…"
                        className="input-edge pl-9"
                        data-testid="products-search"
                    />
                </div>
                <select
                    value={brandFilter}
                    onChange={(e) => setBrandFilter(e.target.value)}
                    className="input-edge w-auto py-2"
                    data-testid="brand-filter"
                >
                    <option value="">Todas as marcas</option>
                    {brands.map((b) => (
                        <option key={b} value={b}>{b}</option>
                    ))}
                </select>
                <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="input-edge w-auto py-2"
                    data-testid="category-filter"
                >
                    <option value="">Todos os tipos</option>
                    {categories.map((c) => (
                        <option key={c.slug} value={c.slug}>{c.name}</option>
                    ))}
                </select>
                {(brandFilter || categoryFilter || search) && (
                    <button
                        onClick={() => { setSearch(""); setBrandFilter(""); setCategoryFilter(""); }}
                        className="btn-ghost text-xs"
                        data-testid="clear-filters-btn"
                    >
                        <X size={14} /> Limpar
                    </button>
                )}
                <span className="font-mono text-xs text-ink/60 ml-auto" data-testid="admin-results-count">
                    {total} produtos
                </span>
            </div>

            <div className="border-2 border-ink bg-white overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-ink text-white text-xs font-mono uppercase tracking-wider">
                        <tr>
                            <th className="text-left p-3">SKU</th>
                            <th className="text-left p-3">Nome</th>
                            <th className="text-left p-3">Marca</th>
                            <th className="text-left p-3">Cat.</th>
                            <th className="text-right p-3">Preço</th>
                            <th className="text-right p-3">Stock</th>
                            <th className="text-right p-3">Acções</th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.map((p) => (
                            <tr key={p.id} className="border-t-2 border-ink/10 hover:bg-magenta/5" data-testid={`product-row-${p.sku}`}>
                                <td className="p-3 font-mono text-xs">{p.sku}</td>
                                <td className="p-3 font-bold">{p.name}</td>
                                <td className="p-3">{p.brand}</td>
                                <td className="p-3 text-xs font-mono">{p.category_slug}</td>
                                <td className="p-3 text-right font-mono">{fmtEUR(p.price)}</td>
                                <td className={`p-3 text-right font-mono ${p.stock <= 0 ? "text-destructive" : ""}`}>
                                    {p.stock}
                                </td>
                                <td className="p-3 text-right">
                                    <button onClick={() => setEditing(p)} className="p-1 hover:text-magenta" data-testid={`edit-${p.sku}`}>
                                        <Pencil size={14} />
                                    </button>
                                    <button onClick={() => del(p.id)} className="p-1 hover:text-destructive ml-1" data-testid={`delete-${p.sku}`}>
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {products.length === 0 && (
                            <tr>
                                <td colSpan={7} className="p-8 text-center text-ink/50 font-mono text-sm">
                                    Sem produtos para os filtros seleccionados.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {total > PAGE_SIZE && (
                <div className="mt-6 flex items-center justify-between flex-wrap gap-3" data-testid="admin-pagination">
                    <div className="font-mono text-xs text-ink/60">
                        Página {page} de {Math.ceil(total / PAGE_SIZE)} · {total} produtos
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="admin-prev-page-btn"
                        >
                            ← Anterior
                        </button>
                        {(() => {
                            const totalPages = Math.ceil(total / PAGE_SIZE);
                            const start = Math.max(1, page - 2);
                            const end = Math.min(totalPages, start + 4);
                            const pages = [];
                            for (let i = start; i <= end; i++) pages.push(i);
                            return pages.map((n) => (
                                <button
                                    key={n}
                                    onClick={() => setPage(n)}
                                    className={`min-w-[40px] h-10 border-2 border-ink font-mono font-bold ${
                                        n === page ? "bg-magenta text-white border-magenta" : "bg-white hover:bg-ink hover:text-white"
                                    }`}
                                    data-testid={`admin-page-${n}`}
                                >
                                    {n}
                                </button>
                            ));
                        })()}
                        <button
                            onClick={() => setPage((p) => Math.min(Math.ceil(total / PAGE_SIZE), p + 1))}
                            disabled={page >= Math.ceil(total / PAGE_SIZE)}
                            className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="admin-next-page-btn"
                        >
                            Seguinte →
                        </button>
                    </div>
                </div>
            )}

            {editing && (
                <ProductModal
                    initial={editing}
                    categories={categories}
                    onClose={() => setEditing(null)}
                    onSave={save}
                />
            )}

            {bulkOpen && (
                <BulkMarginModal
                    brands={brands}
                    categories={categories}
                    defaultBrand={brandFilter}
                    defaultCategory={categoryFilter}
                    onClose={() => setBulkOpen(false)}
                    onApplied={load}
                />
            )}
        </div>
    );
}

function ProductModal({ initial, categories, onClose, onSave }) {
    const [form, setForm] = useState({
        ...initial,
        compatibility: Array.isArray(initial.compatibility) ? initial.compatibility.join(", ") : initial.compatibility || "",
    });
    const upd = (k) => (e) =>
        setForm((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    return (
        <div className="fixed inset-0 bg-ink/60 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="product-modal">
            <div className="bg-white border-2 border-ink w-full max-w-3xl shadow-[8px_8px_0_0_#0A0A0A] my-10">
                <div className="p-5 border-b-2 border-ink flex items-center justify-between">
                    <h2 className="font-display font-black text-2xl">
                        {form.id ? "Editar produto" : "Novo produto"}
                    </h2>
                    <button onClick={onClose} className="p-2 hover:text-magenta">
                        <X size={20} />
                    </button>
                </div>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        onSave(form);
                    }}
                    className="p-6 grid sm:grid-cols-2 gap-4"
                >
                    <Field label="SKU *" v={form.sku} on={upd("sku")} required />
                    <Field label="Marca" v={form.brand} on={upd("brand")} />
                    <div className="sm:col-span-2">
                        <Field label="Nome *" v={form.name} on={upd("name")} required />
                    </div>
                    <div>
                        <span className="label-mono mb-2 block">Categoria</span>
                        <select className="input-edge" value={form.category_slug} onChange={upd("category_slug")}>
                            <option value="">—</option>
                            {categories.map((c) => (
                                <option key={c.slug} value={c.slug}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    <Field label="Preço VENDA s/IVA (€) *" type="number" step="0.01" v={form.price} on={upd("price")} required />
                    <Field label="Preço CUSTO s/IVA (€)" type="number" step="0.01" v={form.cost_price ?? ""} on={upd("cost_price")} />
                    <Field label="PVR — Preço Recomendado de Venda s/IVA (€)" type="number" step="0.01" v={form.supplier_price ?? ""} on={upd("supplier_price")} />
                    <Field label="Stock *" type="number" v={form.stock} on={upd("stock")} required />
                    <ProfitLine price={form.price} cost={form.cost_price} pvr={form.supplier_price} />
                    <div className="grid sm:grid-cols-2 gap-3 sm:col-span-2">
                        <Field
                            label="Cor"
                            v={form.color || ""}
                            on={upd("color")}
                            placeholder="ex: preto, ciano"
                        />
                        <div className="grid grid-cols-2 gap-2">
                            <Field
                                label="Cópias / ml"
                                type="number"
                                step="0.01"
                                v={form.yield_value ?? ""}
                                on={upd("yield_value")}
                            />
                            <label className="block">
                                <span className="label-mono mb-2 block">Unidade</span>
                                <select
                                    value={form.yield_unit || ""}
                                    onChange={upd("yield_unit")}
                                    className="input-edge"
                                    data-testid="yield-unit-select"
                                >
                                    <option value="">—</option>
                                    <option value="paginas">páginas/cópias</option>
                                    <option value="ml">ml</option>
                                </select>
                            </label>
                        </div>
                    </div>
                    {form.supplier_name && (
                        <div className="sm:col-span-2 border-2 border-ink/30 bg-paper p-3">
                            <div className="label-mono text-magenta mb-1">Origem fornecedor</div>
                            <div className="font-mono text-xs text-ink/80 grid sm:grid-cols-3 gap-2">
                                <span>Fornecedor: <strong>{form.supplier_name}</strong></span>
                                <span>Ref. fornecedor: <strong>{form.supplier_code || "—"}</strong></span>
                                <span>Último sync: <strong>{form.last_sync_at ? new Date(form.last_sync_at).toLocaleString("pt-PT") : "—"}</strong></span>
                            </div>
                        </div>
                    )}
                    <div className="sm:col-span-2">
                        <span className="label-mono mb-2 block">Imagem do produto</span>
                        <FileUpload value={form.image_url} onChange={(v) => setForm((s) => ({ ...s, image_url: v }))} label="" />
                    </div>
                    <div className="sm:col-span-2">
                        <Field label="Compatibilidades (separar com vírgula)" v={form.compatibility} on={upd("compatibility")} />
                    </div>
                    <div className="sm:col-span-2">
                        <span className="label-mono mb-2 block">Descrição</span>
                        <textarea rows={4} className="input-edge" value={form.description} onChange={upd("description")} />
                    </div>

                    {/* Sync flags block */}
                    <div className="sm:col-span-2 border-2 border-magenta/40 bg-magenta/5 p-4 space-y-3">
                        <div className="label-mono text-magenta">Protecções de sincronização</div>
                        <p className="text-[11px] font-mono text-ink/60">
                            Marque o que <strong>NÃO</strong> deve ser actualizado automaticamente nos próximos syncs do fornecedor.
                        </p>
                        <label className="flex items-center gap-2 border-2 border-ink p-2 bg-white">
                            <input type="checkbox" checked={!!form.sync_no_all} onChange={upd("sync_no_all")} className="accent-magenta w-4 h-4" data-testid="sync-no-all" />
                            <span className="text-sm font-bold uppercase">🚫 Não actualizar NADA (master switch)</span>
                        </label>
                        <div className="grid sm:grid-cols-2 gap-2 pl-3 border-l-2 border-magenta/30">
                            {[
                                { k: "sync_no_stock", l: "Não actualizar stock" },
                                { k: "sync_no_price", l: "Não actualizar preço de venda" },
                                { k: "sync_no_cost", l: "Não actualizar preço de custo" },
                                { k: "sync_no_name", l: "Não actualizar nome" },
                                { k: "sync_no_printers", l: "Não actualizar impressoras" },
                                { k: "sync_no_data", l: "Não actualizar dados (descr, img, cor)" },
                            ].map((f) => (
                                <label key={f.k} className={`flex items-center gap-2 ${form.sync_no_all ? "opacity-40" : ""}`}>
                                    <input type="checkbox" checked={!!form[f.k]} disabled={!!form.sync_no_all} onChange={upd(f.k)} className="accent-magenta w-4 h-4" data-testid={f.k.replace(/_/g, "-")} />
                                    <span className="text-xs font-bold uppercase">{f.l}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <label className="flex items-center gap-2">
                        <input type="checkbox" checked={!!form.is_original} onChange={upd("is_original")} className="accent-magenta w-4 h-4" />
                        <span className="text-sm font-bold uppercase">Original</span>
                    </label>
                    <label className="flex items-center gap-2">
                        <input type="checkbox" checked={!!form.is_refurbished} onChange={upd("is_refurbished")} className="accent-magenta w-4 h-4" />
                        <span className="text-sm font-bold uppercase">Recondicionado</span>
                    </label>
                    <label className="flex items-center gap-2">
                        <input type="checkbox" checked={!!form.is_featured} onChange={upd("is_featured")} className="accent-magenta w-4 h-4" />
                        <span className="text-sm font-bold uppercase">Destacado</span>
                    </label>
                    <label className="flex items-center gap-2 sm:col-span-2 border-t-2 border-ink pt-3">
                        <input type="checkbox" checked={!!form.active} onChange={upd("active")} className="accent-magenta w-4 h-4" data-testid="active-toggle" />
                        <span className="text-sm font-bold uppercase">Activo (visível na loja)</span>
                        {form.manually_disabled && !form.active && (
                            <span className="ml-2 text-[10px] font-mono bg-destructive text-white px-2 py-0.5">DESACTIVADO MANUALMENTE — sync nunca reactiva</span>
                        )}
                    </label>

                    <div className="sm:col-span-2 flex gap-3 justify-end pt-4 border-t-2 border-ink">
                        <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
                        <button type="submit" className="btn-primary" data-testid="save-product-btn">Guardar</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Field({ label, v, on, type = "text", step, required, placeholder }) {
    return (
        <label className="block">
            <span className="label-mono mb-2 block">{label}</span>
            <input
                type={type}
                step={step}
                value={v}
                onChange={on}
                required={required}
                placeholder={placeholder}
                className="input-edge"
            />
        </label>
    );
}

function ProfitLine({ price, cost, pvr }) {
    const p = Number(price || 0);
    const c = Number(cost || 0);
    const r = Number(pvr || 0);
    if (!p || !c) {
        return (
            <div className="sm:col-span-2 -mt-2 text-[11px] font-mono text-ink/40">
                Indique preço de custo para ver lucro.
            </div>
        );
    }
    const lucro = p - c;
    const pct = c > 0 ? (lucro / c) * 100 : 0;
    const negative = lucro < 0;
    const pvrDelta = r > 0 ? p - r : null;
    return (
        <div
            className={`sm:col-span-2 -mt-2 text-xs font-mono px-3 py-2 border-2 ${
                negative ? "bg-red-50 border-destructive text-destructive" : "bg-accent-green/10 border-accent-green/40 text-ink/80"
            }`}
            data-testid="profit-line"
        >
            {negative && <span className="font-bold mr-2">⚠ ATENÇÃO:</span>}
            Lucro: <strong>{lucro.toFixed(2)} €</strong> ({pct.toFixed(0)}%)
            {pvrDelta !== null && (
                <span className="ml-3 text-ink/50">
                    {" · "}
                    {pvrDelta < 0 ? "−" : "+"}
                    {Math.abs(pvrDelta).toFixed(2)} € vs PVR
                </span>
            )}
        </div>
    );
}
